import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {App, Button, Empty, Popconfirm, Space, Table, Tooltip, Typography} from 'antd';
import React, {useRef} from 'react';
import SectionHeader from '../../../components/common/SectionHeader';
import TableToolbar, {useTableToolbar} from '../../../components/common/TableToolbar';
import {useDeleteScaleBand, useScaleBands} from '../../../hooks/useScaleBands';
import {useTranslation} from '../../../hooks/useTranslation';
import {extractErrorMessage} from '../../../utils/apiError';

const {Text} = Typography;

interface ScaleBandTableProps {
  onCreate: () => void;
  onEdit: (record: any) => void;
  drawerOpen: boolean;
}

const ScaleBandTable: React.FC<ScaleBandTableProps> = ({onCreate, onEdit, drawerOpen}) => {
  const {t} = useTranslation();
  const {message: messageApi} = App.useApp();

  // Deletes used to `.catch(console.error)`: the backend answered every failure
  // with HTTP 200, so nothing rejected and the user saw no feedback at all.
  // Now that failures arrive as 4xx/5xx, surface the server's message.
  const reportDeleteError = (error: unknown) =>
    messageApi.error(extractErrorMessage(error, t('messages.deleteError')));
  const {
    data: scaleBands = [],
    isLoading,
    isFetching,
    refetch,
  } = useScaleBands();
  const deleteMutation = useDeleteScaleBand();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const columns = [
    {
      title: t('settings.content.impactAssessment.formulaTable.columns.displayOrder'),
      dataIndex: 'displayOrder',
      width: 80,
      key: 'displayOrder',
      render: (order: number | null) =>
        order != null
          ? <span className="gt-ac-num-pill">{order}</span>
          : '-',
    },
    {
      title: t('settings.content.impactAssessment.formulaTable.columns.color'),
      dataIndex: 'color',
      width: 140,
      key: 'color',
      render: (color: string) => {
        if (!color) return '-';
        const display = color.startsWith('#') ? color.toUpperCase() : `#${color.toUpperCase()}`;
        return (
          <div className="gt-ac-color-cell">
            <span className="gt-ac-color-swatch" style={{backgroundColor: color}}/>
            <Text code className="gt-ac-color-hex">{display}</Text>
          </div>
        );
      },
    },
    {
      title: t('settings.content.impactAssessment.formulaTable.columns.description'),
      dataIndex: 'label',
      width: 200,
      key: 'label',
      render: (label: string) => (
        <span className="gt-ac-band-label">{label || '-'}</span>
      ),
    },
    {
      title: t('settings.content.impactAssessment.formulaTable.columns.formula'),
      dataIndex: 'formula',
      key: 'formula',
      render: (formula: string) =>
        formula
          ? <Text code className="gt-ac-formula-cell">{formula}</Text>
          : <span className="gt-ac-muted">—</span>,
    },
    {
      title: t('settings.content.impactAssessment.formulaTable.columns.actions'),
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title={t('settings.content.impactAssessment.formulaTable.actions.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined/>}
              onClick={() => onEdit(record)}
              aria-label={t('settings.content.impactAssessment.formulaTable.actions.edit')}
            />
          </Tooltip>
          <Popconfirm
            title={t('settings.content.impactAssessment.formulaTable.actions.deleteConfirm')}
            okText={t('settings.content.impactAssessment.formulaTable.actions.delete')}
            cancelText={t('settings.content.impactAssessment.formulaTable.actions.cancel')}
            okButtonProps={{danger: true, loading: deleteMutation.isPending}}
            onConfirm={() => deleteMutation.mutateAsync(record.id).catch(reportDeleteError)}
          >
            <Tooltip title={t('settings.content.impactAssessment.formulaTable.actions.delete')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined/>}
                aria-label={t('settings.content.impactAssessment.formulaTable.actions.delete')}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const {
    size,
    setSize,
    visibleColumnKeys,
    setVisibleColumnKeys,
    visibleColumns,
  } = useTableToolbar(columns);

  return (
    <SectionHeader
      divider={false}
      title={t('settings.content.impactAssessment.sections.scaleBands.title')}
      description={t('settings.content.impactAssessment.sections.scaleBands.description')}
      extra={
        <Button
          color="primary"
          variant="text"
          size="medium"
          icon={<PlusOutlined/>}
          onClick={onCreate}
          disabled={drawerOpen}
        >
          {t('settings.content.impactAssessment.formulaTable.actions.add')}
        </Button>
      }
    >
      <div ref={tableRef} style={{background: 'var(--ant-color-bg-container, #fff)'}}>
        <TableToolbar
          columns={columns}
          visibleColumnKeys={visibleColumnKeys}
          onVisibleColumnKeysChange={setVisibleColumnKeys}
          size={size}
          onSizeChange={setSize}
          onReload={() => refetch()}
          isLoading={isFetching}
          fullscreenTarget={tableRef}
        />
        <Table
          bordered
          size={size}
          dataSource={scaleBands}
          columns={visibleColumns}
          pagination={false}
          loading={isLoading}
          rowKey={(record: any) => String(record.id)}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('settings.content.impactAssessment.formulaTable.messages.noDataFound')}
              >
                <Button
                  color="default"
                  variant="filled"
                  icon={<PlusOutlined/>}
                  onClick={onCreate}
                  disabled={drawerOpen}
                >
                  {t('settings.content.impactAssessment.formulaTable.actions.add')}
                </Button>
              </Empty>
            ),
          }}
        />
      </div>
    </SectionHeader>
  );
};

export default ScaleBandTable;
